import { ExperienceCreationCard } from "@src/components/Dialogs/CreateExperienceDialog/types";
import { ExperienceCategory } from "@intelligems/ig-types";
import { IconButton, Stack, useTheme } from "@mui/material";
import { IgButton, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { officialCategoryName } from "@intelligems/ig-utils";
import { TestCreateGraphic } from "@src/components/Dialogs/CreateExperienceDialog/graphics/TestCreateGraphic";
import { PersonalizationCreateGraphic } from "@src/components/Dialogs/CreateExperienceDialog/graphics/PersonalizationCreateGraphic";
import Grid from "@mui/material/Grid";
import { ArrowLeft, X } from "lucide-react";

interface CategorySelectionPageProps {
  card: ExperienceCreationCard;
  getRunModeDisabledReason: (
    card: ExperienceCreationCard,
    category: ExperienceCategory
  ) => string | undefined;
  onBack: () => void;
  onClose: () => void;
  onRunMode: (
    card: ExperienceCreationCard,
    category: ExperienceCategory
  ) => void;
}
export const CategorySelectionPage = ({
  card,
  getRunModeDisabledReason,
  onBack,
  onClose,
  onRunMode,
}: CategorySelectionPageProps) => {
  const theme = useTheme();

  const renderModeCard = (category: ExperienceCategory) => {
    const disabledReason = getRunModeDisabledReason(card, category);

    return (
      <Stack
        spacing={5}
        sx={{
          flex: 1,
          p: 5,
          border: `1px solid ${theme.igBorders.core.disabled}`,
          borderRadius: "12px",
          minWidth: 0,
        }}
      >
        <Stack spacing={2} sx={{ minHeight: 76 }}>
          <IgTypography
            variant="body2"
            color={theme.palette.text.secondary}
            sx={{ textTransform: "uppercase" }}
            fontWeight={500}
          >
            {officialCategoryName(category, true)}
          </IgTypography>
          <IgTypography variant="h2" fontWeight={600}>
            {category === "experiment"
              ? "“I’m not sure what works. Let the data decide.”"
              : "“I already know who should see what.”"}
          </IgTypography>
        </Stack>
        {category === "experiment" ? (
          <TestCreateGraphic sx={{ width: "100%", height: "237px" }} />
        ) : (
          <PersonalizationCreateGraphic
            sx={{ width: "100%", height: "237px" }}
          />
        )}
        <IgTypography variant="body2" color={theme.palette.text.tertiary}>
          {category === "experiment"
            ? "Visitors are split between test groups. Results show which version performs better."
            : "Show your chosen version to everyone or a specific audience. Nothing is being tested."}
        </IgTypography>

        <IgTooltip title={disabledReason ?? ""} placement="top">
          <IgButton
            fullWidth
            variant={category === "experiment" ? "contained" : "outlined"}
            disabled={!!disabledReason}
            onClick={() => onRunMode(card, category)}
          >
            {category === "experiment"
              ? "Run a Test"
              : "Create a Personalization"}
          </IgButton>
        </IgTooltip>
      </Stack>
    );
  };

  return (
    <Stack spacing={8} sx={{ p: 6, width: 850 }}>
      <Grid container alignItems="center" wrap="nowrap" columnSpacing={2}>
        <Grid width={100}>
          <IgButton
            variant="text"
            color="secondary"
            startIcon={<ArrowLeft size={16} />}
            onClick={onBack}
          >
            Back
          </IgButton>
        </Grid>
        <Grid flex={1} textAlign="center">
          <IgTypography variant="h2" fontWeight={600}>
            How do you want to run this experience?
          </IgTypography>
        </Grid>
        <Grid width={100} textAlign="right">
          <IconButton onClick={onClose} size="small">
            <X size={24} />
          </IconButton>
        </Grid>
      </Grid>
      <Stack direction="row" spacing={6}>
        {renderModeCard("experiment")}
        {renderModeCard("personalization")}
      </Stack>
      <IgTypography textAlign="center" color={theme.palette.text.placeholder}>
        <IgTypography
          sx={{ fontWeight: 600, color: theme.palette.text.tertiary }}
        >
          Not sure?
        </IgTypography>{" "}
        Start with a test. You can roll out the winner to everyone or a specific
        audience later.
      </IgTypography>
    </Stack>
  );
};
